import {
  standards as seedStandards,
  elements as seedElements,
  candidates as seedCandidates,
  groups as seedGroups,
  expedientes as seedExpedientes,
  sessionsSeed,
  evaluadores,
  dictaminadores,
  auditores,
  instructores,
  diagnosticItems as seedDiagnosticBank,
  teoricoQuestions as seedTheoreticalBank,
  type Standard,
  type Element as StandardElement,
  type Candidate,
  type Group,
  type GroupSession,
  type Expediente,
  type StaffMember,
} from "./index";
import type { Role } from "@/store/useDemoStore";

export interface DiagnosticBankItem { id: string; area: string; pregunta: string; }
export interface TheoreticalBankQuestion { id: string; enunciado: string; opciones: string[]; correcta: number; }
export interface ExpedienteDoc {
  id: string;
  nombre: string;
  tipo: "video" | "foto" | "documento" | "audio";
  obligatoria: boolean;
  estado: "faltante" | "cargado" | "observado";
  subidoEn?: string;
  observacion?: string;
  criterioIds?: string[];
}
export type ExamKind = "diagnostico" | "teorico";
export interface ExamAttempt {
  id: string;
  expedienteId: string;
  candidatoId: string;
  estandar: string;
  kind: ExamKind;
  fecha: string;
  score: number;
  aprobado: boolean;
}

export interface PrechecklistItem { id: string; label: string; critical?: boolean; }
export interface PrechecklistEntry { itemId: string; checked: boolean; nota?: string; }
export interface PrechecklistState {
  entries: PrechecklistEntry[];
  completedAt?: string;
  evaluadorId?: string;
}
export type PracticalResultado = "competente" | "todavia-no";
export interface PracticalScore {
  resultado: PracticalResultado;
  nota?: string;
  evidenciaUrl?: string;
  evaluadorId?: string;
  fecha?: string;
}

export type AttendanceEstado = "presente" | "ausente" | "tarde";
export interface AttendanceMark {
  estado: AttendanceEstado;
  nota?: string;
  registradoEn?: string;
}
export type AttendanceBySession = Record<string, Record<string, AttendanceMark>>;

export type GroupMaterialTipo = "video" | "documento" | "enlace" | "presentacion" | "lectura";
export interface GroupMaterial {
  id: string;
  titulo: string;
  url: string;
  tipo: GroupMaterialTipo;
  creadoEn: string;
  autorId?: string;
}

export interface CandidateObservation {
  id: string;
  candidatoId: string;
  grupoId: string;
  autorId: string;
  ts: string;
  texto: string;
}

export interface ExamAvailability {
  diagnostico: boolean;
  teorico: boolean;
  habilitadoEn?: string;
  habilitadoPor?: string;
}

// ===== LMS (Ruta formativa) =====
export type LmsModuleKind = "leccion" | "simulador-preop" | "evaluacion-final";

export interface LmsQuizQuestion {
  id: string;
  enunciado: string;
  opciones: string[];
  correcta: number;
  explicacion?: string;
}

export interface LmsBlock {
  id: string;
  titulo: string;
  descripcion: string;
  orden: number;
}

export interface LmsModule {
  id: string;
  blockId: string;
  orden: number;
  titulo: string;
  descripcion: string;
  kind: LmsModuleKind;
  duracionMin: number;
  videoUrl?: string;
  contenido: string[];
  quiz: LmsQuizQuestion[];
  criteriosCubiertos?: string[];
  elementoId?: string;
}

export interface LmsRoute {
  estandar: string;
  publicada: boolean;
  blocks: LmsBlock[];
  modules: LmsModule[];
}

export interface LmsAttempt {
  id: string;
  moduloId: string;
  ts: string;
  aciertos: number;
  total: number;
  aprobado: boolean;
  respuestas: number[];
}

export interface LmsProgressState {
  attemptsByModule: Record<string, LmsAttempt[]>;
  completedModuleIds: string[];
  rutaCompletadaEn?: string;
}

export type PreopEstado = "cumple" | "no-cumple" | "no-aplica";
export interface PreopChecklistEntry {
  itemId: string;
  estado: PreopEstado;
  observacion?: string;
}
export interface PreopChecklistRun {
  id: string;
  expedienteId: string;
  moduloId: string;
  fecha: string;
  entries: PreopChecklistEntry[];
  completas: number;
  total: number;
  criticasFaltantes: number;
  aprobado: boolean;
}

export type RuleFamily = "RG" | "RE" | "RL" | "RT" | "RP" | "RV" | "RD";
export type RuleSeverity = "bloqueante" | "advertencia";
export type RuleOperator = ">=" | "<=" | "==" | "!=" | ">" | "<";

export interface RuleCondition {
  field: string;
  operator: RuleOperator;
  value: number | string | boolean;
  unit?: string;
}

export interface Rule {
  id: string;
  estandar: string;
  familia: RuleFamily;
  nombre: string;
  descripcion: string;
  severidad: RuleSeverity;
  condicion: RuleCondition;
  habilitada: boolean;
}

export interface AppUser {
  id: string;
  nombre: string;
  email: string;
  curp?: string;
  rol: Role;
  roles: Role[];
  estado: "activo" | "inactivo";
  capacidad?: number;
  iniciales: string;
  cedula?: string;
  notas?: string;
  foto?: string;
}

export interface AuditLogEntry {
  id: string;
  ts: string;
  actor: string;
  rol: Role;
  accion: string;
  entidad: string;
  entidadId: string;
}

export type EvaluationStatus = "borrador" | "pre-checklist" | "en-curso" | "concluida" | "anulada";
export interface EvaluationScore {
  criterioId: string;
  valor: "competente" | "todavia-no" | null;
  notas?: string;
}
export interface Evaluation {
  id: string;
  expedienteId: string;
  candidatoId: string;
  evaluadorId: string;
  estandar: string;
  elementoId?: string;
  instrumentoId?: string;
  estado: EvaluationStatus;
  fecha: string;
  scores: EvaluationScore[];
  observaciones?: string;
}

export type DictamenResultado = "competente" | "todavia-no" | "pendiente";
export interface DictamenRevocacion {
  motivo: string;
  fecha: string;
  actor: string;
}
export interface Dictamen {
  id: string;
  expedienteId: string;
  candidatoId: string;
  estandar: string;
  dictaminadorId: string;
  resultado: DictamenResultado;
  fundamento: string;
  fecha: string;
  certificadoId?: string;
  recomendacionIA?: DictamenResultado;
  revocacion?: DictamenRevocacion;
}

function staffToUser(s: StaffMember, rol: Role): AppUser {
  const isEval = rol === "evaluador";
  return {
    id: s.id,
    nombre: s.nombre,
    email: `${s.id}@certicore.mx`,
    rol,
    roles: [rol],
    estado: "activo",
    iniciales: s.iniciales,
    cedula: s.cedula,
    capacidad: isEval ? 12 : undefined,
    notas: s.rol,
  };
}

function candidateToUser(c: Candidate): AppUser {
  return {
    id: c.id,
    nombre: c.nombre,
    email: c.email,
    curp: c.curp,
    rol: "alumno",
    roles: ["alumno"],
    estado: "activo",
    iniciales: c.iniciales,
  };
}

export function defaultRulesForStandard(estandar: string): Rule[] {
  if (estandar !== "EC1781") {
    return [
      { id: `rg-${estandar}-1`, estandar, familia: "RG", nombre: "Expediente con todos los pasos completos", descripcion: "Diagnóstico, teórico y práctica aplicados", severidad: "bloqueante", condicion: { field: "expediente.completo", operator: "==", value: true }, habilitada: true },
      { id: `rd-${estandar}-1`, estandar, familia: "RD", nombre: "Criterios competentes ≥ 90%", descripcion: "Decisión final del dictamen", severidad: "bloqueante", condicion: { field: "evaluacion.porcentaje_competente", operator: ">=", value: 90, unit: "%" }, habilitada: true },
    ];
  }
  return [
    { id: "rg-ec1781-1", estandar, familia: "RG", nombre: "Expediente completo", descripcion: "Diagnóstico, teórico y práctica aplicados", severidad: "bloqueante", condicion: { field: "expediente.completo", operator: "==", value: true }, habilitada: true },
    { id: "rg-ec1781-2", estandar, familia: "RG", nombre: "Vigencia del estándar", descripcion: "El estándar debe estar publicado", severidad: "bloqueante", condicion: { field: "estandar.estado", operator: "==", value: "publicado" }, habilitada: true },
    { id: "re-ec1781-1", estandar, familia: "RE", nombre: "Evidencias requeridas entregadas", descripcion: "6 de 6 evidencias", severidad: "bloqueante", condicion: { field: "evidencias.entregadas", operator: ">=", value: 6 }, habilitada: true },
    { id: "re-ec1781-2", estandar, familia: "RE", nombre: "Video integral verificado", descripcion: "Video del recorrido completo", severidad: "advertencia", condicion: { field: "evidencias.video_verificado", operator: "==", value: true }, habilitada: true },
    { id: "rl-ec1781-1", estandar, familia: "RL", nombre: "Examen médico vigente", descripcion: "Apto para servicio operativo", severidad: "bloqueante", condicion: { field: "candidato.examen_medico_vigente", operator: "==", value: true }, habilitada: true },
    { id: "rt-ec1781-1", estandar, familia: "RT", nombre: "Distancia de frenado dentro de parámetro", descripcion: "≤ 32 m a 60 km/h", severidad: "bloqueante", condicion: { field: "telemetria.frenado_m", operator: "<=", value: 32, unit: "m" }, habilitada: true },
    { id: "rt-ec1781-2", estandar, familia: "RT", nombre: "Calificación teórica ≥ 80%", descripcion: "Umbral mínimo de aprobación teórica", severidad: "bloqueante", condicion: { field: "examen_teorico.puntaje", operator: ">=", value: 80, unit: "%" }, habilitada: true },
    { id: "rp-ec1781-1", estandar, familia: "RP", nombre: "Prechecklist aplicada", descripcion: "Lista preoperativa firmada", severidad: "bloqueante", condicion: { field: "prechecklist.aplicada", operator: "==", value: true }, habilitada: true },
    { id: "rv-ec1781-1", estandar, familia: "RV", nombre: "Telemetría coincide con bitácora", descripcion: "Validación cruzada GPS / bitácora", severidad: "advertencia", condicion: { field: "validacion.telemetria_vs_bitacora", operator: "==", value: true }, habilitada: true },
    { id: "rd-ec1781-1", estandar, familia: "RD", nombre: "Criterios competentes ≥ 90%", descripcion: "Decisión final del dictamen", severidad: "bloqueante", condicion: { field: "evaluacion.porcentaje_competente", operator: ">=", value: 90, unit: "%" }, habilitada: true },
  ];
}

export interface SeedState {
  standards: Record<string, Standard>;
  archivedStandards: Record<string, Standard>;
  elementsByStandard: Record<string, StandardElement[]>;
  instrumentsByStandard: Record<string, import("./index").Instrumento[]>;
  rulesByStandard: Record<string, Rule[]>;
  groups: Record<string, Group>;
  sessionsByGroup: Record<string, GroupSession[]>;
  candidates: Record<string, Candidate>;
  expedientes: Record<string, Expediente>;
  evaluations: Record<string, Evaluation>;
  dictamenes: Record<string, Dictamen>;
  users: Record<string, AppUser>;
  auditLog: AuditLogEntry[];
  enrollments: Record<string, string[]>;
  expedienteDocs: Record<string, ExpedienteDoc[]>;
  examAttempts: Record<string, ExamAttempt[]>;
  diagnosticBankByStandard: Record<string, DiagnosticBankItem[]>;
  theoreticalBankByStandard: Record<string, TheoreticalBankQuestion[]>;
  prechecklistTemplateByStandard: Record<string, PrechecklistItem[]>;
  prechecklistByExpediente: Record<string, PrechecklistState>;
  practicalScoresByExpediente: Record<string, Record<string, PracticalScore>>;
  practicalNotesByExpediente: Record<string, string>;
  attendanceByGroup: Record<string, AttendanceBySession>;
  progressByExpediente: Record<string, Record<string, boolean>>;
  materialsByGroup: Record<string, GroupMaterial[]>;
  observationsByCandidate: Record<string, CandidateObservation[]>;
  examAvailabilityByExpediente: Record<string, ExamAvailability>;
  lmsRouteByStandard: Record<string, LmsRoute>;
  lmsProgressByExpediente: Record<string, LmsProgressState>;
  preopRunsByExpediente: Record<string, PreopChecklistRun[]>;
}

// ===== LMS seed: Ruta formativa EC1781 =====
const LMS_PREOP_ITEMS: { id: string; label: string; critical?: boolean }[] = [
  { id: "lp1", label: "Presión y estado de neumático delantero", critical: true },
  { id: "lp2", label: "Presión y estado de neumático trasero", critical: true },
  { id: "lp3", label: "Nivel de aceite del motor" },
  { id: "lp4", label: "Líquido de frenos delantero y trasero", critical: true },
  { id: "lp5", label: "Funcionamiento de luces (alta, baja, direccionales, freno)" },
  { id: "lp6", label: "Bocina y sirena operativa" },
  { id: "lp7", label: "Tensión y lubricación de cadena" },
  { id: "lp8", label: "Casco, guantes, botas, protección dorsal", critical: true },
  { id: "lp9", label: "Radio operativo con batería al 100%" },
  { id: "lp10", label: "Bitácora preoperativa firmada por instructor" },
];

function buildLmsRouteEc1781(): LmsRoute {
  const blocks: LmsBlock[] = [
    { id: "b1", orden: 1, titulo: "Introducción al estándar EC1781", descripcion: "Marco general, propósito y alcance de la certificación." },
    { id: "b2", orden: 2, titulo: "Seguridad y prevención", descripcion: "Equipo de protección personal y reducción de riesgos." },
    { id: "b3", orden: 3, titulo: "Revisión preoperativa de motocicleta", descripcion: "Inspección sistemática previa a la operación." },
    { id: "b4", orden: 4, titulo: "Entrega-recepción y reportes", descripcion: "Documentación, bitácora y reportes de novedades." },
    { id: "b5", orden: 5, titulo: "Conducción de alto desempeño", descripcion: "Postura, frenado, trazado y conducción defensiva." },
    { id: "b6", orden: 6, titulo: "Maniobras avanzadas y escolta", descripcion: "Maniobras evasivas, formación táctica y patrullaje." },
    { id: "b7", orden: 7, titulo: "Evaluación integral", descripcion: "Repaso transversal y autoevaluación final." },
  ];

  const modules: LmsModule[] = [
    {
      id: "m1", blockId: "b1", orden: 1, kind: "leccion", duracionMin: 12,
      titulo: "Bienvenida y propósito del EC1781",
      descripcion: "Qué evalúa el estándar y qué se espera del candidato.",
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      contenido: [
        "El estándar EC1781 evalúa la competencia para conducir motocicletas en servicios policiales y de seguridad pública con alto desempeño.",
        "Se compone de dos elementos: E5637 (preparación y revisión) y E5638 (operación y maniobras).",
        "El candidato es evaluado en 17 criterios distribuidos entre ambos elementos, con énfasis en seguridad y respuesta táctica.",
      ],
      quiz: [
        { id: "q1a", enunciado: "¿Cuántos elementos componen el estándar EC1781?", opciones: ["1", "2", "3", "4"], correcta: 1, explicacion: "Son dos elementos: E5637 y E5638." },
        { id: "q1b", enunciado: "El estándar EC1781 aplica principalmente a:", opciones: ["Motociclismo recreativo", "Motociclistas de seguridad pública y servicios policiales", "Mecánicos de motocicletas", "Instructores de manejo civil"], correcta: 1 },
        { id: "q1c", enunciado: "¿Cuántos criterios se evalúan en total?", opciones: ["10", "12", "17", "20"], correcta: 2, explicacion: "Suman 17 criterios entre E5637 y E5638." },
      ],
      criteriosCubiertos: ["c-e5637-d1"],
    },
    {
      id: "m2", blockId: "b1", orden: 2, kind: "leccion", duracionMin: 10,
      titulo: "Marco normativo del CONOCER",
      descripcion: "Cómo se publica, audita y vigila un estándar de competencia.",
      contenido: [
        "CONOCER (Consejo Nacional de Normalización y Certificación de Competencias Laborales) emite los estándares EC.",
        "Una ECE (Entidad de Certificación y Evaluación) aplica las evaluaciones y emite el certificado.",
        "El expediente es revisado por dictaminador y puede ser auditado en cualquier momento del ciclo de vigencia.",
      ],
      quiz: [
        { id: "q2a", enunciado: "¿Quién emite el certificado de competencia?", opciones: ["El propio candidato", "La ECE acreditada", "El instructor", "El cliente del servicio"], correcta: 1 },
        { id: "q2b", enunciado: "El órgano que normaliza el estándar es:", opciones: ["SEP", "CONOCER", "STPS", "INEGI"], correcta: 1 },
        { id: "q2c", enunciado: "Una auditoría puede ocurrir:", opciones: ["Solo antes del dictamen", "Solo después de la entrega", "En cualquier momento del ciclo de vigencia", "Nunca"], correcta: 2 },
      ],
    },
    {
      id: "m3", blockId: "b2", orden: 1, kind: "leccion", duracionMin: 14, elementoId: "elem-e5637",
      titulo: "Equipo de protección personal",
      descripcion: "Componentes obligatorios del EPP y criterios de aceptación.",
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      contenido: [
        "El EPP mínimo incluye: casco integral certificado, guantes, botas con protección de tobillo, chaqueta con protectores y protección dorsal.",
        "El casco debe contar con certificación DOT o equivalente y estar libre de impactos previos.",
        "El uso parcial o incorrecto del EPP es causal de no aprobación inmediata en la evaluación práctica.",
      ],
      quiz: [
        { id: "q3a", enunciado: "El casco debe estar certificado bajo norma:", opciones: ["ISO 9001", "DOT o equivalente", "NOM-035", "ASTM-D"], correcta: 1 },
        { id: "q3b", enunciado: "Un casco con impacto previo debe:", opciones: ["Repararse", "Reemplazarse", "Pintarse", "Cubrirse con calcomanías"], correcta: 1 },
        { id: "q3c", enunciado: "¿El uso incompleto del EPP en la evaluación práctica…?", opciones: ["Se permite si hay calor", "Se permite con autorización verbal", "Es causal de no aprobación", "No tiene efecto"], correcta: 2 },
      ],
      criteriosCubiertos: ["c-e5637-d7"],
    },
    {
      id: "m4", blockId: "b2", orden: 2, kind: "leccion", duracionMin: 11, elementoId: "elem-e5637",
      titulo: "Riesgos y reducción de accidentes",
      descripcion: "Identificación de factores críticos y técnicas de mitigación.",
      contenido: [
        "Los tres factores principales de accidente son: velocidad inadecuada, distracción y falta de mantenimiento preventivo.",
        "La regla de los 2 segundos define la distancia mínima al vehículo precedente en condiciones normales.",
        "En lluvia, la distancia debe duplicarse y la velocidad reducirse al menos 20%.",
      ],
      quiz: [
        { id: "q4a", enunciado: "La regla de los 2 segundos se refiere a:", opciones: ["Tiempo de reacción del motor", "Distancia mínima al vehículo de adelante", "Tiempo de calentamiento", "Duración del frenado"], correcta: 1 },
        { id: "q4b", enunciado: "En lluvia, la distancia de seguridad debe:", opciones: ["Mantenerse igual", "Duplicarse", "Reducirse", "Eliminarse"], correcta: 1 },
        { id: "q4c", enunciado: "El factor menos común de los siguientes en accidentes es:", opciones: ["Velocidad inadecuada", "Distracción", "Falta de mantenimiento", "Color del casco"], correcta: 3 },
      ],
    },
    {
      id: "m5", blockId: "b3", orden: 1, kind: "leccion", duracionMin: 16, elementoId: "elem-e5637",
      titulo: "Inspección preoperativa: criterios y secuencia",
      descripcion: "Cómo recorrer la motocicleta de forma sistemática.",
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      contenido: [
        "La inspección se realiza en sentido horario partiendo desde el lado izquierdo trasero.",
        "Los puntos críticos son: neumáticos (presión y desgaste), frenos (líquido y desgaste de pastillas), luces y nivel de aceite.",
        "Cualquier hallazgo crítico debe ser registrado en la bitácora y reportado al instructor antes de operar.",
      ],
      quiz: [
        { id: "q5a", enunciado: "El recorrido recomendado de inspección es:", opciones: ["Aleatorio", "Horario desde el lado izquierdo trasero", "Solo frontal", "Solo trasero"], correcta: 1 },
        { id: "q5b", enunciado: "¿Cuál NO es un punto crítico?", opciones: ["Neumáticos", "Frenos", "Color del tanque", "Luces"], correcta: 2 },
        { id: "q5c", enunciado: "Un hallazgo crítico debe:", opciones: ["Ignorarse si hay prisa", "Registrarse y reportarse antes de operar", "Resolverse en marcha", "Anotarse después del turno"], correcta: 1 },
      ],
      criteriosCubiertos: ["c-e5637-d2", "c-e5637-a1"],
    },
    {
      id: "m6", blockId: "b3", orden: 2, kind: "simulador-preop", duracionMin: 8, elementoId: "elem-e5637",
      titulo: "Simulador: Revisión preoperativa",
      descripcion: "Aplica la lista de cotejo paso a paso en un caso simulado.",
      contenido: [
        "Marca cada ítem inspeccionado. Los ítems críticos no pueden faltar.",
        "Se considera aprobado si: 0 críticas faltantes y al menos 80% de ítems revisados.",
      ],
      quiz: [],
      criteriosCubiertos: ["c-e5637-d2", "c-e5637-d3", "c-e5637-d4", "c-e5637-d5"],
    },
    {
      id: "m7", blockId: "b4", orden: 1, kind: "leccion", duracionMin: 10, elementoId: "elem-e5637",
      titulo: "Documento de entrega-recepción",
      descripcion: "Qué se firma al recibir y entregar la unidad.",
      contenido: [
        "Se documentan: kilometraje, nivel de combustible, herramientas presentes y novedades visibles.",
        "Cualquier daño preexistente debe fotografiarse y firmarse por entrante y saliente.",
        "El formato firmado se archiva en bitácora y es evidencia para auditoría.",
      ],
      quiz: [
        { id: "q7a", enunciado: "¿Qué se documenta en entrega-recepción?", opciones: ["Solo combustible", "Solo kilometraje", "Kilometraje, combustible, herramientas y novedades", "Solo daños"], correcta: 2 },
        { id: "q7b", enunciado: "Un daño preexistente debe:", opciones: ["Ignorarse", "Fotografiarse y firmarse por ambos", "Repararse antes de salir", "Anotarse solo verbalmente"], correcta: 1 },
        { id: "q7c", enunciado: "El formato firmado se usa como:", opciones: ["Decoración", "Evidencia para auditoría", "Recibo de pago", "Solicitud de vacaciones"], correcta: 1 },
      ],
      criteriosCubiertos: ["c-e5637-p1"],
    },
    {
      id: "m8", blockId: "b4", orden: 2, kind: "leccion", duracionMin: 8, elementoId: "elem-e5637",
      titulo: "Reporte de daños y novedades",
      descripcion: "Cómo escalar incidencias durante el turno.",
      contenido: [
        "Un incidente menor se reporta al supervisor inmediato dentro del turno; uno mayor se reporta en el momento.",
        "El reporte incluye: hora, ubicación GPS, descripción y fotografía si es seguro tomarla.",
        "La omisión de reporte puede invalidar la evaluación práctica del candidato.",
      ],
      quiz: [
        { id: "q8a", enunciado: "Un incidente mayor se reporta:", opciones: ["Al día siguiente", "En el momento", "Al final del mes", "Solo si lo piden"], correcta: 1 },
        { id: "q8b", enunciado: "El reporte debe incluir:", opciones: ["Solo descripción", "Hora, ubicación, descripción y foto si es seguro", "Solo foto", "Solo hora"], correcta: 1 },
        { id: "q8c", enunciado: "La omisión de reporte puede:", opciones: ["No tener efecto", "Mejorar la calificación", "Invalidar la evaluación práctica", "Pagarse aparte"], correcta: 2 },
      ],
    },
    {
      id: "m9", blockId: "b5", orden: 1, kind: "leccion", duracionMin: 18, elementoId: "elem-e5638",
      titulo: "Conducción defensiva en patrullaje",
      descripcion: "Postura, visión periférica y anticipación.",
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      contenido: [
        "La postura correcta mantiene espalda recta, codos ligeramente flexionados y mirada a 12 segundos por delante.",
        "La visión periférica se entrena con barridos sistemáticos (espejo izq, frente, espejo der, frente).",
        "Anticipar al menos dos vehículos por delante reduce el tiempo de reacción crítico.",
      ],
      quiz: [
        { id: "q9a", enunciado: "La mirada en conducción defensiva debe estar a:", opciones: ["1 segundo adelante", "12 segundos adelante", "30 segundos adelante", "Sólo en el espejo"], correcta: 1 },
        { id: "q9b", enunciado: "Anticipar al menos N vehículos adelante reduce el tiempo de reacción. N =", opciones: ["1", "2", "5", "10"], correcta: 1 },
        { id: "q9c", enunciado: "El barrido visual debe ser:", opciones: ["Aleatorio", "Sistemático: espejo izq, frente, espejo der, frente", "Sólo frente", "Sólo espejos"], correcta: 1 },
      ],
      criteriosCubiertos: ["c-e5638-d1", "c-e5638-d3"],
    },
    {
      id: "m10", blockId: "b5", orden: 2, kind: "leccion", duracionMin: 14, elementoId: "elem-e5638",
      titulo: "Manejo de velocidad y frenado",
      descripcion: "Distancia de frenado, ABS y técnicas de detención.",
      contenido: [
        "La distancia de frenado a 60 km/h en pavimento seco debe ser ≤ 32 m (criterio RT del estándar).",
        "El freno delantero aporta ~70% de la capacidad de detención; debe aplicarse progresivamente.",
        "El ABS no reduce la distancia en seco; reduce el riesgo de bloqueo en superficie de baja adherencia.",
      ],
      quiz: [
        { id: "q10a", enunciado: "Distancia máxima de frenado a 60 km/h en seco:", opciones: ["≤ 20 m", "≤ 32 m", "≤ 50 m", "Sin límite"], correcta: 1 },
        { id: "q10b", enunciado: "¿Qué porcentaje aporta el freno delantero?", opciones: ["20%", "40%", "~70%", "100%"], correcta: 2 },
        { id: "q10c", enunciado: "El ABS principalmente:", opciones: ["Acelera más", "Reduce el riesgo de bloqueo en baja adherencia", "Reemplaza el freno trasero", "Sólo sirve en lluvia"], correcta: 1 },
      ],
      criteriosCubiertos: ["c-e5638-d4", "c-e5638-d5"],
    },
    {
      id: "m11", blockId: "b6", orden: 1, kind: "leccion", duracionMin: 16, elementoId: "elem-e5638",
      titulo: "Maniobras evasivas",
      descripcion: "Esquivar obstáculos manteniendo control.",
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      contenido: [
        "La maniobra básica de esquive se ejecuta con movimiento del manillar opuesto al sentido deseado (contramanillar).",
        "La velocidad ideal para entrenar esquives es 30-40 km/h.",
        "Después de un esquive, se debe recuperar la trayectoria original antes de 3 segundos.",
      ],
      quiz: [
        { id: "q11a", enunciado: "El contramanillar consiste en:", opciones: ["Empujar el manillar del lado opuesto al giro deseado", "Empujar del lado del giro", "Soltar el manillar", "Frenar fuerte"], correcta: 0 },
        { id: "q11b", enunciado: "Velocidad recomendada de entrenamiento de esquives:", opciones: ["10 km/h", "30-40 km/h", "80 km/h", "Más de 100 km/h"], correcta: 1 },
        { id: "q11c", enunciado: "La trayectoria debe recuperarse en menos de:", opciones: ["1 segundo", "3 segundos", "10 segundos", "1 minuto"], correcta: 1 },
      ],
      criteriosCubiertos: ["c-e5638-d6", "c-e5638-d7"],
    },
    {
      id: "m12", blockId: "b6", orden: 2, kind: "leccion", duracionMin: 13, elementoId: "elem-e5638",
      titulo: "Escolta y formación táctica",
      descripcion: "Posiciones de escolta y comunicación por radio.",
      contenido: [
        "La formación básica de escolta es la 'V' con punta adelante y dos motociclistas en flancos a 45°.",
        "La comunicación por radio se hace en frases cortas: 'unidad-mensaje-confirmar'.",
        "Los relevos de posición se anuncian con al menos 5 segundos de anticipación.",
      ],
      quiz: [
        { id: "q12a", enunciado: "La formación básica de escolta es:", opciones: ["Línea recta", "V con punta adelante", "Cuadrado", "Círculo"], correcta: 1 },
        { id: "q12b", enunciado: "Estructura del mensaje por radio:", opciones: ["Frase larga libre", "Unidad-mensaje-confirmar", "Sólo confirmación", "Sin estructura"], correcta: 1 },
        { id: "q12c", enunciado: "Un relevo de posición se anuncia con al menos:", opciones: ["1 seg", "5 seg", "30 seg", "No se anuncia"], correcta: 1 },
      ],
    },
    {
      id: "m13", blockId: "b7", orden: 1, kind: "leccion", duracionMin: 10,
      titulo: "Repaso transversal",
      descripcion: "Síntesis de los conceptos clave del estándar.",
      contenido: [
        "Recuerda: EPP completo + preoperativa + conducción defensiva + reporte oportuno = aprobación práctica.",
        "Cada criterio que se marca 'todavía no' requiere un plan de mejora documentado.",
        "El expediente debe quedar con todas las evidencias verificadas antes del dictamen.",
      ],
      quiz: [
        { id: "q13a", enunciado: "Un criterio 'todavía no' requiere:", opciones: ["Repetir el estándar completo", "Plan de mejora documentado", "Cambiar de evaluador", "Ignorarse"], correcta: 1 },
        { id: "q13b", enunciado: "Antes del dictamen, las evidencias deben estar:", opciones: ["En cualquier estado", "Verificadas", "Sólo cargadas", "Sólo firmadas"], correcta: 1 },
        { id: "q13c", enunciado: "Para aprobar práctica se necesita combinar:", opciones: ["Sólo EPP", "EPP + preoperativa + conducción defensiva + reporte", "Sólo conducción", "Sólo reporte"], correcta: 1 },
      ],
    },
    {
      id: "m14", blockId: "b7", orden: 2, kind: "evaluacion-final", duracionMin: 12,
      titulo: "Autoevaluación final",
      descripcion: "Prueba integradora de 3 preguntas para cerrar la ruta.",
      contenido: [
        "Esta autoevaluación no reemplaza al examen teórico oficial, pero te indica si estás listo para presentarlo.",
      ],
      quiz: [
        { id: "q14a", enunciado: "El EC1781 evalúa…", opciones: ["Mantenimiento de motocicletas", "Competencia para conducir motocicletas en seguridad pública", "Ventas de motocicletas", "Reparación de motores"], correcta: 1 },
        { id: "q14b", enunciado: "Para aprobar un módulo del LMS se requiere:", opciones: ["1 de 3", "2 de 3 o más", "Todas correctas", "Ninguna"], correcta: 1 },
        { id: "q14c", enunciado: "El umbral global de la ruta para considerarse 'lista para evaluación' es:", opciones: ["50%", "60%", "80%", "100%"], correcta: 2 },
      ],
    },
  ];

  return { estandar: "EC1781", publicada: true, blocks, modules };
}

export const LMS_QUIZ_PASS_THRESHOLD = 2; // 2 of 3
export const LMS_ROUTE_READY_PCT = 80;
export const LMS_PREOP_ITEMS_EC1781 = LMS_PREOP_ITEMS;

const DEFAULT_PRECHECKLIST_EC1781: PrechecklistItem[] = [
  { id: "p1", label: "Presión y estado de neumático delantero", critical: true },
  { id: "p2", label: "Presión y estado de neumático trasero", critical: true },
  { id: "p3", label: "Nivel de aceite del motor" },
  { id: "p4", label: "Líquido de frenos delantero y trasero", critical: true },
  { id: "p5", label: "Funcionamiento de luces (alta, baja, direccionales, freno)" },
  { id: "p6", label: "Bocina y sirena operativa" },
  { id: "p7", label: "Tensión y lubricación de cadena" },
  { id: "p8", label: "Casco, guantes, botas, protección dorsal", critical: true },
  { id: "p9", label: "Radio operativo con batería al 100%" },
  { id: "p10", label: "Bitácora preoperativa firmada por instructor" },
];

export interface BuildSeedOptions {
  includeEc1781?: boolean;
}

export function buildSeed(opts: BuildSeedOptions = {}): SeedState {
  const includeEc1781 = opts.includeEc1781 ?? false;
  const isEc = (estandar: string) => estandar === "EC1781";
  const keepStd = (estandar: string) => includeEc1781 || !isEc(estandar);

  // Identify EC1781-only candidate IDs (those whose only enrollment is via EC1781 groups/expedientes).
  const ec1781CandIds = new Set<string>();
  for (const g of seedGroups) if (isEc(g.estandar)) for (const id of g.candidatosIds) ec1781CandIds.add(id);
  for (const e of seedExpedientes) if (isEc(e.estandar)) ec1781CandIds.add(e.candidatoId);
  const nonEcCandIds = new Set<string>();
  for (const g of seedGroups) if (!isEc(g.estandar)) for (const id of g.candidatosIds) nonEcCandIds.add(id);
  for (const e of seedExpedientes) if (!isEc(e.estandar)) nonEcCandIds.add(e.candidatoId);
  const keepCand = (id: string) => includeEc1781 || !ec1781CandIds.has(id) || nonEcCandIds.has(id);

  const groupIdsKept = new Set<string>();
  for (const g of seedGroups) if (keepStd(g.estandar)) groupIdsKept.add(g.id);
  const keepGroup = (grupoId: string) => groupIdsKept.has(grupoId);

  const standardsRec: Record<string, Standard> = {};
  for (const s of seedStandards) if (keepStd(s.codigo)) standardsRec[s.codigo] = { ...s };

  const elementsByStandard: Record<string, StandardElement[]> = {};
  const instrumentsByStandard: Record<string, import("./index").Instrumento[]> = {};
  for (const e of seedElements) {
    if (!keepStd(e.estandar)) continue;
    (elementsByStandard[e.estandar] ??= []).push({
      ...e,
      criterios: e.criterios.map((c) => ({ ...c })),
      evidencias: e.evidencias.map((ev) => ({ ...ev })),
      instrumentos: e.instrumentos.map((i) => ({ ...i })),
    });
    for (const inst of e.instrumentos) {
      (instrumentsByStandard[e.estandar] ??= []).push({ ...inst });
    }
  }

  const rulesByStandard: Record<string, Rule[]> = {};
  for (const code of Object.keys(standardsRec)) {
    rulesByStandard[code] = defaultRulesForStandard(code);
  }

  const groupsRec: Record<string, Group> = {};
  for (const g of seedGroups) {
    if (!keepStd(g.estandar)) continue;
    groupsRec[g.id] = {
      ...g,
      candidatosIds: [...g.candidatosIds],
      evaluadoresIds: g.evaluadoresIds ? [...g.evaluadoresIds] : [g.evaluadorId],
      documentos: g.documentos ? g.documentos.map((d) => ({ ...d })) : [],
    };
  }
  const sessionsByGroup: Record<string, GroupSession[]> = {};
  for (const s of sessionsSeed) if (keepGroup(s.grupoId)) (sessionsByGroup[s.grupoId] ??= []).push({ ...s });

  const candidatesRec: Record<string, Candidate> = {};
  for (const c of seedCandidates) if (keepCand(c.id)) candidatesRec[c.id] = { ...c };

  const expedientesRec: Record<string, Expediente> = {};
  for (const e of seedExpedientes) if (keepStd(e.estandar)) expedientesRec[e.id] = { ...e };

  const users: Record<string, AppUser> = {};
  users["admin-001"] = {
    id: "admin-001",
    nombre: "Mtra. Alicia Aguirre",
    email: "alicia.aguirre@certicore.mx",
    rol: "admin",
    roles: ["admin"],
    estado: "activo",
    iniciales: "AA",
    notas: "Administradora académica",
  };
  for (const e of evaluadores) users[e.id] = staffToUser(e, "evaluador");
  for (const d of dictaminadores) users[d.id] = staffToUser(d, "dictaminador");
  for (const a of auditores) users[a.id] = staffToUser(a, "auditor");
  for (const i of instructores) users[i.id] = staffToUser(i, "instructor");
  for (const c of seedCandidates) if (keepCand(c.id)) users[c.id] = candidateToUser(c);

  // Enrollments derived from expedientes (candidate -> standards)
  const enrollments: Record<string, string[]> = {};
  for (const e of seedExpedientes) {
    if (!keepStd(e.estandar)) continue;
    (enrollments[e.candidatoId] ??= []);
    if (!enrollments[e.candidatoId].includes(e.estandar)) enrollments[e.candidatoId].push(e.estandar);
  }

  // Expediente docs derived from each standard's element evidencias
  const expedienteDocs: Record<string, ExpedienteDoc[]> = {};
  for (const exp of seedExpedientes) {
    if (!keepStd(exp.estandar)) continue;
    const els = elementsByStandard[exp.estandar] ?? [];
    const allEv = els.flatMap((el) => el.evidencias);
    const cargadoIds = new Set<string>();
    // Mark the first N evidences as cargado/observed based on entregadas/verificadas
    for (let i = 0; i < Math.min(exp.evidencias.entregadas, allEv.length); i++) {
      cargadoIds.add(allEv[i].id);
    }
    expedienteDocs[exp.id] = allEv.map((ev, i) => {
      const cargado = cargadoIds.has(ev.id);
      const observado = cargado && i >= exp.evidencias.verificadas;
      return {
        id: `${exp.id}-${ev.id}`,
        nombre: ev.nombre,
        tipo: ev.tipo,
        obligatoria: ev.obligatoria,
        criterioIds: ev.criterioIds,
        estado: observado ? "observado" : cargado ? "cargado" : "faltante",
        subidoEn: cargado ? exp.evaluacionPractica.fecha !== "—" ? exp.evaluacionPractica.fecha : exp.examenTeorico.fecha : undefined,
        observacion: observado ? "Pendiente de verificación por el evaluador." : undefined,
      };
    });
  }

  // Exam attempts derived from expediente state
  const examAttempts: Record<string, ExamAttempt[]> = {};
  for (const exp of seedExpedientes) {
    if (!keepStd(exp.estandar)) continue;
    const arr: ExamAttempt[] = [];
    if (exp.diagnostico.aplicado && exp.diagnostico.puntaje != null) {
      arr.push({
        id: `att-${exp.id}-diag-1`,
        expedienteId: exp.id,
        candidatoId: exp.candidatoId,
        estandar: exp.estandar,
        kind: "diagnostico",
        fecha: exp.diagnostico.fecha,
        score: exp.diagnostico.puntaje,
        aprobado: true,
      });
    }
    if (exp.examenTeorico.aplicado && exp.examenTeorico.puntaje != null) {
      arr.push({
        id: `att-${exp.id}-teo-1`,
        expedienteId: exp.id,
        candidatoId: exp.candidatoId,
        estandar: exp.estandar,
        kind: "teorico",
        fecha: exp.examenTeorico.fecha,
        score: exp.examenTeorico.puntaje,
        aprobado: exp.examenTeorico.aprobado,
      });
    }
    if (arr.length) examAttempts[exp.id] = arr;
  }

  // Question banks seeded for EC1781
  const diagnosticBankByStandard: Record<string, DiagnosticBankItem[]> = {};
  const theoreticalBankByStandard: Record<string, TheoreticalBankQuestion[]> = {};
  const prechecklistTemplateByStandard: Record<string, PrechecklistItem[]> = {};
  if (includeEc1781) {
    diagnosticBankByStandard.EC1781 = seedDiagnosticBank.map((d) => ({ ...d }));
    theoreticalBankByStandard.EC1781 = seedTheoreticalBank.map((q) => ({ ...q, opciones: [...q.opciones] }));
    prechecklistTemplateByStandard.EC1781 = DEFAULT_PRECHECKLIST_EC1781.map((i) => ({ ...i }));
  }

  // Per-expediente prechecklist and practical scores seeded from expediente aggregates
  const prechecklistByExpediente: Record<string, PrechecklistState> = {};
  const practicalScoresByExpediente: Record<string, Record<string, PracticalScore>> = {};
  const practicalNotesByExpediente: Record<string, string> = {};
  for (const exp of seedExpedientes) {
    if (!keepStd(exp.estandar)) continue;
    const tpl = prechecklistTemplateByStandard[exp.estandar] ?? [];
    if (exp.evaluacionPractica.aplicada) {
      prechecklistByExpediente[exp.id] = {
        entries: tpl.map((i) => ({ itemId: i.id, checked: true })),
        completedAt: exp.evaluacionPractica.fecha !== "—" ? exp.evaluacionPractica.fecha : undefined,
      };
      // Score practical criteria: first N "competente", remainder "todavia-no".
      const allCrits = (elementsByStandard[exp.estandar] ?? []).flatMap((el) => el.criterios.map((c) => ({ ...c, elementId: el.id })));
      const compCount = exp.evaluacionPractica.criteriosCompetente;
      const scores: Record<string, PracticalScore> = {};
      allCrits.forEach((c, i) => {
        scores[c.id] = {
          resultado: i < compCount ? "competente" : "todavia-no",
          nota: i < compCount ? undefined : "Repetir maniobra; tiempo fuera de parámetro.",
          fecha: exp.evaluacionPractica.fecha !== "—" ? exp.evaluacionPractica.fecha : undefined,
        };
      });
      practicalScoresByExpediente[exp.id] = scores;
    }
  }

  const lmsRouteByStandard: Record<string, LmsRoute> = {};
  if (includeEc1781) lmsRouteByStandard.EC1781 = buildLmsRouteEc1781();
  // Seed initial LMS progress for existing expedientes to make the demo feel "alive".
  const lmsProgressByExpediente: Record<string, LmsProgressState> = {};
  const ec1781Route = lmsRouteByStandard.EC1781;
  for (const exp of seedExpedientes) {
    if (exp.estandar !== "EC1781" || !includeEc1781 || !ec1781Route) continue;
    // Seed progress proportional to candidate.progreso
    const cand = seedCandidates.find((c) => c.id === exp.candidatoId);
    const candProgreso = cand?.progreso ?? 0;
    const total = ec1781Route.modules.length;
    const target = Math.max(0, Math.min(total, Math.round(candProgreso / 100 * total)));
    const completedModuleIds: string[] = [];
    const attemptsByModule: Record<string, LmsAttempt[]> = {};
    for (let i = 0; i < target; i++) {
      const mod = ec1781Route.modules[i];
      if (mod.kind === "leccion" || mod.kind === "evaluacion-final") {
        const respuestas = mod.quiz.map((q) => q.correcta);
        attemptsByModule[mod.id] = [
          {
            id: `att-lms-${exp.id}-${mod.id}`,
            moduloId: mod.id,
            ts: new Date().toISOString(),
            aciertos: mod.quiz.length,
            total: mod.quiz.length,
            aprobado: true,
            respuestas,
          },
        ];
      }
      completedModuleIds.push(mod.id);
    }
    lmsProgressByExpediente[exp.id] = {
      attemptsByModule,
      completedModuleIds,
      rutaCompletadaEn: target >= total ? new Date().toISOString() : undefined,
    };
  }
  const preopRunsByExpediente: Record<string, PreopChecklistRun[]> = {};

  // Reconcile standard counters with seeded data (e.g. EC1781 ends up with 12 candidates, 2 certificates).
  for (const code of Object.keys(standardsRec)) {
    const exps = Object.values(expedientesRec).filter((e) => e.estandar === code);
    if (exps.length === 0) continue;
    standardsRec[code] = {
      ...standardsRec[code],
      candidatosActivos: exps.length,
      certificadosEmitidos: exps.filter((e) => e.certificado.emitido).length,
    };
  }

  return {
    standards: standardsRec,
    archivedStandards: {},
    elementsByStandard,
    instrumentsByStandard,
    rulesByStandard,
    groups: groupsRec,
    sessionsByGroup,
    candidates: candidatesRec,
    expedientes: expedientesRec,
    evaluations: {},
    dictamenes: {},
    users,
    auditLog: [],
    enrollments,
    expedienteDocs,
    examAttempts,
    diagnosticBankByStandard,
    theoreticalBankByStandard,
    prechecklistTemplateByStandard,
    prechecklistByExpediente,
    practicalScoresByExpediente,
    attendanceByGroup: {},
    progressByExpediente: {},
    materialsByGroup: {},
    observationsByCandidate: {},
    examAvailabilityByExpediente: {},
    practicalNotesByExpediente,
    lmsRouteByStandard,
    lmsProgressByExpediente,
    preopRunsByExpediente,
  };
}

// ===== EC1781 bundle: poblado bajo demanda al dar de alta el estándar en el wizard. =====
// Devuelve TODAS las piezas dependientes del EC1781 (grupos, candidatos, expedientes,
// LMS, exámenes, etc.) listas para fusionarse en el estado del store. Los criterios
// reales del estándar parseado por la IA se pasan vía `realElements` para que las
// puntuaciones prácticas y los documentos del expediente queden ligados a los IDs
// reales (no a IDs ficticios).
export function buildEc1781Bundle(realElements: StandardElement[]): {
  standardPatch: Partial<Standard> & { candidatosActivos: number; certificadosEmitidos: number };
  groups: Record<string, Group>;
  sessionsByGroup: Record<string, GroupSession[]>;
  candidates: Record<string, Candidate>;
  expedientes: Record<string, Expediente>;
  users: Record<string, AppUser>;
  enrollments: Record<string, string[]>;
  expedienteDocs: Record<string, ExpedienteDoc[]>;
  examAttempts: Record<string, ExamAttempt[]>;
  diagnosticBank: DiagnosticBankItem[];
  theoreticalBank: TheoreticalBankQuestion[];
  prechecklistTemplate: PrechecklistItem[];
  prechecklistByExpediente: Record<string, PrechecklistState>;
  practicalScoresByExpediente: Record<string, Record<string, PracticalScore>>;
  lmsRoute: LmsRoute;
  lmsProgressByExpediente: Record<string, LmsProgressState>;
} {
  // Use full seed (with EC1781) to get the canonical shapes, then override derived
  // pieces (docs, practical scores) using the real parsed elements.
  const full = buildSeed({ includeEc1781: true });

  const ecExpIds = Object.values(full.expedientes)
    .filter((e) => e.estandar === "EC1781")
    .map((e) => e.id);
  const ecCandIds = new Set<string>();
  for (const id of ecExpIds) ecCandIds.add(full.expedientes[id].candidatoId);
  for (const g of Object.values(full.groups)) if (g.estandar === "EC1781") for (const c of g.candidatosIds) ecCandIds.add(c);
  const ecGroupIds = Object.values(full.groups).filter((g) => g.estandar === "EC1781").map((g) => g.id);

  const pick = <T,>(rec: Record<string, T>, ids: string[]): Record<string, T> => {
    const out: Record<string, T> = {};
    for (const id of ids) if (rec[id]) out[id] = rec[id];
    return out;
  };

  const groups = pick(full.groups, ecGroupIds);
  const sessionsByGroup = pick(full.sessionsByGroup, ecGroupIds);
  const candidates = pick(full.candidates, [...ecCandIds]);
  const expedientes = pick(full.expedientes, ecExpIds);
  const users: Record<string, AppUser> = {};
  for (const id of ecCandIds) if (full.users[id]) users[id] = full.users[id];

  const enrollments: Record<string, string[]> = {};
  for (const id of ecCandIds) enrollments[id] = ["EC1781"];

  // Recompute expediente docs against REAL evidences
  const realAllEv = realElements.flatMap((el) => el.evidencias);
  const expedienteDocs: Record<string, ExpedienteDoc[]> = {};
  for (const expId of ecExpIds) {
    const exp = expedientes[expId];
    const cargadoIds = new Set<string>();
    for (let i = 0; i < Math.min(exp.evidencias.entregadas, realAllEv.length); i++) {
      cargadoIds.add(realAllEv[i].id);
    }
    expedienteDocs[expId] = realAllEv.map((ev, i) => {
      const cargado = cargadoIds.has(ev.id);
      const observado = cargado && i >= exp.evidencias.verificadas;
      return {
        id: `${expId}-${ev.id}`,
        nombre: ev.nombre,
        tipo: ev.tipo,
        obligatoria: ev.obligatoria,
        criterioIds: ev.criterioIds,
        estado: observado ? "observado" : cargado ? "cargado" : "faltante",
        subidoEn: cargado ? (exp.evaluacionPractica.fecha !== "—" ? exp.evaluacionPractica.fecha : exp.examenTeorico.fecha) : undefined,
        observacion: observado ? "Pendiente de verificación por el evaluador." : undefined,
      };
    });
  }

  const examAttempts = pick(full.examAttempts, ecExpIds);

  // Recompute practical scores against REAL criteria IDs
  const allCrits = realElements.flatMap((el) => el.criterios.map((c) => ({ ...c, elementId: el.id })));
  const prechecklistByExpediente: Record<string, PrechecklistState> = {};
  const practicalScoresByExpediente: Record<string, Record<string, PracticalScore>> = {};
  const tpl = full.prechecklistTemplateByStandard.EC1781 ?? [];
  for (const expId of ecExpIds) {
    const exp = expedientes[expId];
    if (!exp.evaluacionPractica.aplicada) continue;
    prechecklistByExpediente[expId] = {
      entries: tpl.map((i) => ({ itemId: i.id, checked: true })),
      completedAt: exp.evaluacionPractica.fecha !== "—" ? exp.evaluacionPractica.fecha : undefined,
    };
    const compCount = exp.evaluacionPractica.criteriosCompetente;
    const scores: Record<string, PracticalScore> = {};
    allCrits.forEach((c, i) => {
      scores[c.id] = {
        resultado: i < compCount ? "competente" : "todavia-no",
        nota: i < compCount ? undefined : "Repetir maniobra; tiempo fuera de parámetro.",
        fecha: exp.evaluacionPractica.fecha !== "—" ? exp.evaluacionPractica.fecha : undefined,
      };
    });
    practicalScoresByExpediente[expId] = scores;
  }

  return {
    standardPatch: {
      candidatosActivos: Object.keys(candidates).length,
      certificadosEmitidos: Object.values(expedientes).filter((e) => e.certificado.emitido).length,
    },
    groups,
    sessionsByGroup,
    candidates,
    expedientes,
    users,
    enrollments,
    expedienteDocs,
    examAttempts,
    diagnosticBank: full.diagnosticBankByStandard.EC1781 ?? [],
    theoreticalBank: full.theoreticalBankByStandard.EC1781 ?? [],
    prechecklistTemplate: tpl,
    prechecklistByExpediente,
    practicalScoresByExpediente,
    lmsRoute: full.lmsRouteByStandard.EC1781,
    lmsProgressByExpediente: full.lmsProgressByExpediente,
  };
}
